import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useMemo } from 'react';
import { Table, ChevronLeft, ChevronRight, Edit3, Trash2, Cloud, FileText, Repeat, RefreshCw } from 'lucide-react';
import type { CostItem, PaginatedCosts } from './costsService';

function formatBRL(value: number) {
  return value.toLocaleString(getInterfaceLanguage(), { style: 'currency', currency: 'BRL' });
}

function formatUSD(value: number) {
  return `USD ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString(getInterfaceLanguage());
}

const sourceBadge: Record<string, { label: string; className: string }> = {
  auto: { get label() { return translateInterfaceText("AWS"); }, className: 'bg-blue-100 text-blue-700' },
  manual: { get label() { return translateInterfaceText("Manual"); }, className: 'bg-amber-100 text-amber-700' },
};

function RecurringBadge({ item }: { item: CostItem }) {
  useLanguage();
  if (item.recurring && item.recurring_active !== false) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700">
        <Repeat size={10} />
        {translateInterfaceText("Recorrente")}</span>
    );
  }
  if (item.recurring_original_id) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-cyan-100 text-cyan-700">
        <RefreshCw size={10} />
        {translateInterfaceText("Auto-gerado")}</span>
    );
  }
  if (item.recurring && item.recurring_active === false) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">
        <Repeat size={10} />
        {translateInterfaceText("Recorrência desativada")}</span>
    );
  }
  return null;
}

interface CostsTableProps {
  data: PaginatedCosts | null;
  manualItems: CostItem[];
  allItems: CostItem[];
  loading: boolean;
  loadingManual: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onEdit: (item: CostItem) => void;
  onDelete: (item: CostItem) => void;
  onDeactivateRecurring: (item: CostItem) => void;
  selectedType: string;
}

export function CostsTable({ data, manualItems, allItems, loading, loadingManual, page, totalPages, onPageChange, onEdit, onDelete, onDeactivateRecurring, selectedType }: CostsTableProps) {
  useLanguage();
  const items = useMemo(() => data?.data || [], [data]);

  const filteredManualItems = useMemo(() => {
    if (selectedType === 'todos') return manualItems;
    return manualItems.filter((item) => item.provider === selectedType);
  }, [manualItems, selectedType]);

  const filteredItems = useMemo(() => {
    if (selectedType === 'todos') return items;
    return allItems.filter((item) => item.provider === selectedType);
  }, [items, selectedType, allItems]);

  const renderRow = (item: CostItem) => {
    const badge = sourceBadge[item.source_type] || { label: item.source_type, className: 'bg-slate-100 text-slate-600' };
    const isRecurringTemplate = item.recurring && item.recurring_active !== false;
    return (
      <tr key={item.id_cost} className="hover:bg-slate-50/50 transition-colors">
        <td className="px-4 py-3 font-medium text-slate-700 max-w-[200px] truncate">{item.service_name}</td>
        <td className="px-4 py-3 text-slate-600">{item.provider}</td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.className}`}>
            {item.source_type === 'auto' ? <Cloud size={10} /> : <FileText size={10} />}
            {badge.label}
          </span>
        </td>
        <td className="px-4 py-3">
          <RecurringBadge item={item} />
        </td>
        <td className="px-4 py-3 text-right text-slate-600 tabular-nums">
          {item.currencyOriginal === 'BRL' ? formatBRL(item.amountOriginal) : formatUSD(item.amountOriginal)}
        </td>
        <td className="px-4 py-3 text-right font-medium text-slate-700 tabular-nums">{formatBRL(item.amountBRL)}</td>
        <td className="px-4 py-3 text-right text-amber-600 tabular-nums">{formatBRL(item.estimatedTaxBRL)}</td>
        <td className="px-4 py-3 text-right font-bold text-slate-900 tabular-nums">{formatBRL(item.totalWithTaxBRL)}</td>
        <td className="px-4 py-3 text-slate-500">{formatDate(item.period_start)}</td>
        <td className="px-4 py-3">
          <div className="flex items-center justify-center gap-1">
            {isRecurringTemplate && (
              <button
                onClick={() => onDeactivateRecurring(item)}
                className="p-1.5 rounded-lg hover:bg-orange-50 text-slate-400 hover:text-orange-600 transition-colors"
                title={translateInterfaceText("Desativar recorrência")}
              >
                <Repeat size={13} />
              </button>
            )}
            <button onClick={() => onEdit(item)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-blue-600 transition-colors" title={translateInterfaceText("Editar")}>
              <Edit3 size={13} />
            </button>
            <button onClick={() => onDelete(item)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors" title={translateInterfaceText("Excluir")}>
              <Trash2 size={13} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-4">
      {/* SEÇÃO DE DESPESAS MANUAIS */}
      {filteredManualItems.length > 0 && (
        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-50 flex items-center gap-2">
            <FileText size={14} className="text-amber-500" />
            <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Despesas Manuais")}</span>
            <span className="ml-auto text-[11px] text-slate-400 font-medium">{filteredManualItems.length} {" "}{translateInterfaceText("registro(s)")}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50/50">
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Serviço")}</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Provedor")}</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Tipo")}</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Status")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Original")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("BRL")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Impostos")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Total")}</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Período")}</th>
                  <th className="text-center px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider w-28">{translateInterfaceText("Ações")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredManualItems.map(renderRow)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* LISTAGEM COMPLETA COM PAGINAÇÃO */}
      <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-50 flex items-center gap-2">
          <Table size={14} className="text-blue-600" />
          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Todos os Custos")}</span>
          {data && (
            <span className="ml-auto text-[11px] text-slate-400 font-medium">{filteredItems.length} {" "}{translateInterfaceText("registro(s)")}</span>
          )}
        </div>

      {loading ? (
        <div className="p-12 text-center">
          <div className="inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-slate-400 mt-3">{translateInterfaceText("Carregando custos...")}</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="p-12 text-center space-y-2">
          <FileText size={20} className="text-slate-300 mx-auto" />
          <p className="text-sm font-bold text-slate-800">{translateInterfaceText("Nenhum custo encontrado")}</p>
          <p className="text-xs text-slate-400">{translateInterfaceText("Ajuste os filtros ou cadastre uma despesa manual.")}</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50/50">
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Serviço")}</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Provedor")}</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Tipo")}</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Status")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Original")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("BRL")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Impostos")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Total")}</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Período")}</th>
                  <th className="text-center px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider w-28">{translateInterfaceText("Ações")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredItems.map(renderRow)}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-slate-50 flex items-center justify-between">
              <span className="text-[11px] text-slate-400">
                {translateInterfaceText("Página")}{" "}{page} {" "}{translateInterfaceText("de")}{" "}{totalPages}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onPageChange(page - 1)}
                  disabled={page <= 1}
                  className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= totalPages}
                  className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}

export default CostsTable;
